import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button, Select } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../api/firebase/references";
import { fetchPortalPassword } from "../../api/postCall/fetchPortalPassword";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { CredentialDetailsModal } from "../../components/modals/credentialDetailsModal/credentialDetailsModal";
import { fetchClientFromFirebase } from "../../redux/slice/clientConfiguration";
import { fetchCredentialConfig } from "../../redux/slice/credentialConfigSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import { decryptPassword } from "../../utils/SimplePasswordCrypto";
import { CredentialDetailsPanel } from "./components/CredentialDetailsPanel";
import { CredentialsList } from "./components/CredentialsList";
import {
  CredentialWithId,
  DetailsCardItem,
  HealthFacility,
  VisibilityState,
} from "./types/credentialTypes";
import { buildCredentialDetailsData } from "./utils/buildCredentialDetailsData";

const CredentialsConfig = () => {
  const { credentials, isLoading } = useSelector(
    (state: RootState) => state.credentialsConfig,
  );

  const { data: clientName } = useSelector(
    (state: RootState) => state.clientConfiguration,
  );

  const [credentialDetails, setCredentialDetails] =
    useState<CredentialWithId | null>(null);
  const [selectedCredentialId, setSelectedCredentialId] = useState<
    string | null
  >(null);
  const [detailsCardData, setDetailsCardData] = useState<DetailsCardItem[]>([]);
  const [isCredentialDetailsModalOpen, setIsCredentialDetailsModalOpen] =
    useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [passwordState, setPasswordState] = useState<VisibilityState>({
    isVisible: false,
    value: "",
    isLoading: false,
  });
  const [secretKeyState, setSecretKeyState] = useState<VisibilityState>({
    isVisible: false,
    value: "",
    isLoading: false,
  });
  const [, setHealthFacilities] = useState<HealthFacility[]>([]);

  // Changed from selectedOrgId to selectedClientId
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(fetchCredentialConfig());
    dispatch(fetchClientFromFirebase());
    fetchHealthFacility();
  }, []);

  const handleCredentialClick = (credential: CredentialWithId) => {
    if (credential?.id) {
      setSelectedCredentialId(credential?.id);
      setCredentialDetails(credential);
    }
  };

  const handleAddClick = () => {
    setIsEditMode(false);
    setIsCredentialDetailsModalOpen(true);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setIsCredentialDetailsModalOpen(true);
  };

  const fetchHealthFacility = async () => {
    try {
      const healthFacilityData = (await FirestoreService.getDocumentsByQuery(
        FirestoreCollectionReference.healthcareFacility(),
        [],
      )) as HealthFacility[];
      setHealthFacilities(healthFacilityData);
    } catch (error) {
      console.error("Error fetching health facility data:", error);
    }
  };

  // Create client options from clientName data
  const clientOptions = clientName.map((client) => ({
    value: client.DocId ?? "",
    label: client.name ?? "",
  }));

  // Changed from handleOrganizationChange to handleClientChange
  const handleClientChange = (option: any) => {
    setSelectedClientId(option.value);
    setSelectedCredentialId(null);
    setCredentialDetails(null);
    setPasswordState({
      isVisible: false,
      value: "",
      isLoading: false,
    });
    setSecretKeyState({
      isVisible: false,
      value: "",
      isLoading: false,
    });
  };

  // Filter credentials based on selected client instead of organization
  const filteredCredentials =
    credentials?.filter((credential) => {
      if (!selectedClientId) return false;
      return credential.client_id === selectedClientId;
    }) || [];

  const togglePasswordVisibility = async () => {
    if (!passwordState.isVisible && credentialDetails?.credential_id) {
      setPasswordState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetchPortalPassword(
          credentialDetails.credential_id,
        );
        const decryptedPassword = await decryptPassword(response.secret_value);
        setPasswordState({
          isVisible: true,
          value: decryptedPassword,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching or decrypting password:", error);
        setPasswordState((prev) => ({
          ...prev,
          isVisible: false,
          isLoading: false,
        }));
      }
    } else {
      setPasswordState({
        isVisible: false,
        value: "",
        isLoading: false,
      });
    }
  };

  const toggleSecretKeyVisibility = async () => {
    if (!secretKeyState.isVisible && credentialDetails?.secret_key) {
      setSecretKeyState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetchPortalPassword(
          credentialDetails.secret_key,
        );
        const decryptedSecretKey = await decryptPassword(response.secret_value);
        setSecretKeyState({
          isVisible: true,
          value: decryptedSecretKey,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching or decrypting secret key:", error);
        setSecretKeyState((prev) => ({
          ...prev,
          isVisible: false,
          isLoading: false,
        }));
      }
    } else {
      setSecretKeyState({
        isVisible: false,
        value: "",
        isLoading: false,
      });
    }
  };

  // Updated useEffect to work with client selection
  useEffect(() => {
    if (credentials && credentials.length > 0 && selectedClientId) {
      const filteredCreds = credentials.filter(
        (credential) => credential.client_id === selectedClientId,
      );

      if (filteredCreds.length > 0) {
        if (id) {
          const foundCredential = filteredCreds.find((cred) => cred.id === id);
          if (foundCredential) {
            handleCredentialClick(foundCredential);
          } else {
            handleCredentialClick(filteredCreds[0]);
          }
        } else {
          handleCredentialClick(filteredCreds[0]);
        }
      }
    }
  }, [credentials, selectedClientId, id]);

  useEffect(() => {
    setPasswordState({
      isVisible: false,
      value: "",
      isLoading: false,
    });
    setSecretKeyState({
      isVisible: false,
      value: "",
      isLoading: false,
    });
  }, [selectedCredentialId]);

  useEffect(() => {
    if (credentialDetails) {
      setDetailsCardData(buildCredentialDetailsData(credentialDetails));
    }
  }, [credentialDetails]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden p-2">
      <div className="flex h-full flex-col overflow-hidden">
        <div className="border-b border-primaryGray-14 pb-2 text-h11 font-semibold">
          Credentials Configuration
        </div>

        {isLoading ? (
          <div className="h-full flex-1 items-center justify-center">
            <LoaderMessage message="Loading form configurations..." />
          </div>
        ) : (
          <div className="flex w-full flex-1 flex-col gap-3 overflow-hidden">
            <div className="mt-2">
              <Select
                id="client-name"
                onOptionChange={handleClientChange}
                label="Select a Client to view credentials"
                placeholder={
                  selectedClientId
                    ? clientName.find(
                        (client) => client.DocId === selectedClientId,
                      )?.name || "Select Client"
                    : "Select Client"
                }
                options={clientOptions}
              />
            </div>

            {selectedClientId && (
              <>
                <div className="flex justify-end gap-3">
                  <Button
                    buttonType="secondary"
                    size="medium"
                    disabled={false}
                    onClick={handleAddClick}
                  >
                    Add Portal
                  </Button>
                  <Button
                    buttonType="primary"
                    size="medium"
                    disabled={!selectedCredentialId}
                    onClick={handleEditClick}
                  >
                    Edit Portal Details
                  </Button>
                </div>
                <div className="flex h-full w-full flex-1 flex-row gap-3 overflow-hidden">
                  <CredentialsList
                    credentials={filteredCredentials}
                    selectedCredentialId={selectedCredentialId}
                    onCredentialSelect={handleCredentialClick}
                  />
                  <div className="flex h-full w-1/2 flex-col">
                    {filteredCredentials && filteredCredentials.length > 0 ? (
                      <CredentialDetailsPanel
                        detailsCardData={detailsCardData}
                        passwordState={passwordState}
                        secretKeyState={secretKeyState}
                        hasCredentialId={!!credentialDetails?.credential_id}
                        hasSecretKey={!!credentialDetails?.secret_key}
                        onTogglePassword={togglePasswordVisibility}
                        onToggleSecretKey={toggleSecretKeyVisibility}
                      />
                    ) : (
                      <div className="mt-10 flex h-full justify-center text-gray-500">
                        No credentials found for this client
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {isCredentialDetailsModalOpen && (
          <CredentialDetailsModal
            isOpen={isCredentialDetailsModalOpen}
            onClose={() => setIsCredentialDetailsModalOpen(false)}
            isEditMode={isEditMode}
            credentialData={credentialDetails}
            clientId={selectedClientId?.toString() ?? ""}
            client_Name={
              selectedClientId
                ? clientName.find((client) => client.DocId === selectedClientId)
                    ?.name
                : ""
            }
          />
        )}
      </div>
    </div>
  );
};

export default CredentialsConfig;
